import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers,
  Users,
  Building2,
  LayoutDashboard,
  Mail,
  ScrollText,
  RefreshCw,
  ArrowRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { getAdminStats } from '../services/api';
import { cn } from '../lib/utils';

const ACTION_LABELS = {
  invite_sent: 'Invite sent',
  invite_accepted: 'Invite accepted',
  invite_cancelled: 'Invite cancelled',
  invite_resent: 'Invite resent',
  account_created_via_invite: 'Account created via invite',
  workspace_created: 'Workspace created',
  workspace_updated: 'Workspace updated',
  workspace_deleted: 'Workspace deleted',
  member_added: 'Member added',
  member_removed: 'Member removed',
  user_created: 'User created',
  user_updated: 'User updated',
  user_deleted: 'User deleted',
  menu_created: 'Menu created',
  menu_published: 'Menu published',
  menu_deleted: 'Menu deleted',
  channel_created: 'Channel added',
  channel_deleted: 'Channel deleted',
};

function StatCard({ icon: Icon, label, value, color, onClick }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'bg-[#18181B] border border-[#27272A] rounded-lg p-5 text-left w-full transition-colors',
        onClick && 'hover:border-[#3F3F46] cursor-pointer',
        !onClick && 'cursor-default'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', color)}>
          <Icon className="w-5 h-5" />
        </div>
        {onClick && <ArrowRight className="w-4 h-4 text-[#52525B]" />}
      </div>
      <p className="text-2xl font-bold">{value ?? '—'}</p>
      <p className="text-sm text-[#A1A1AA] mt-0.5">{label}</p>
    </button>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getAdminStats();
      setStats(data);
    } catch (e) {
      // silently fail — not critical
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const statCards = [
    { icon: Layers, label: 'Workspaces', value: stats?.workspaces, color: 'bg-emerald-500/10 text-emerald-400', path: '/workspaces' },
    { icon: Users, label: 'Users', value: stats?.users, color: 'bg-blue-500/10 text-blue-400', path: '/users' },
    { icon: Building2, label: 'Channels', value: stats?.channels, color: 'bg-[#06C755]/10 text-[#06C755]', path: null },
    { icon: LayoutDashboard, label: 'Menus', value: stats?.menus, color: 'bg-purple-500/10 text-purple-400', path: null },
    { icon: Mail, label: 'Pending Invites', value: stats?.pendingInvites, color: 'bg-yellow-500/10 text-yellow-400', path: '/workspaces' },
  ];

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">System-wide overview</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-[#27272A]"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
        {statCards.map(({ icon, label, value, color, path }) => (
          <StatCard
            key={label}
            icon={icon}
            label={label}
            value={loading ? '…' : value}
            color={color}
            onClick={path ? () => navigate(path) : null}
          />
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#27272A]">
          <h2 className="font-semibold flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-[#A1A1AA]" />
            Recent Activity
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/logs')}
            className="text-[#A1A1AA] hover:text-white text-xs h-7"
          >
            View all
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>

        {loading ? (
          <div className="divide-y divide-[#27272A]">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-[#27272A]" />
                <div className="flex-1 h-4 bg-[#27272A] rounded animate-pulse" />
                <div className="w-16 h-3 bg-[#27272A] rounded animate-pulse" />
              </div>
            ))}
          </div>
        ) : !stats?.recentActivity?.length ? (
          <div className="px-5 py-8 text-center text-[#52525B] text-sm">No activity yet</div>
        ) : (
          <div className="divide-y divide-[#27272A]">
            {stats.recentActivity.map((log) => (
              <div key={log.id} className="px-5 py-3 flex items-center gap-3 text-sm">
                <div className={cn(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  log.status === 'success' ? 'bg-emerald-500' : 'bg-red-500'
                )} />
                <span className="flex-1 text-[#A1A1AA] truncate">
                  <span className="text-white font-medium">
                    {ACTION_LABELS[log.action] || log.action}
                  </span>
                  {log.targetEmail && (
                    <span className="ml-1">— {log.targetEmail}</span>
                  )}
                </span>
                <span className="text-[#52525B] text-xs flex-shrink-0">{timeAgo(log.timestamp)}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
        {[
          { label: 'Manage Workspaces', path: '/workspaces', icon: Layers },
          { label: 'All Users', path: '/all-users', icon: Users },
          { label: 'Activity Logs', path: '/logs', icon: ScrollText },
        ].map(({ label, path, icon: Icon }) => (
          <button
            key={path}
            onClick={() => navigate(path)}
            className="bg-[#18181B] border border-[#27272A] rounded-lg px-4 py-3 flex items-center gap-3 text-sm text-[#A1A1AA] hover:text-white hover:border-[#3F3F46] transition-colors"
          >
            <Icon className="w-4 h-4" />
            {label}
            <ArrowRight className="w-3 h-3 ml-auto" />
          </button>
        ))}
      </div>
    </div>
  );
}

export default AdminDashboard;
